<script setup lang="ts">
import { ref } from 'vue'

import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useAndroidBackHandler } from '@/composables/useAndroidBackHandler'
import { setDialogRef } from '@/services/dialogService'

interface DialogOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
}

// State
const isOpen = ref(false)
const title = ref('')
const message = ref('')
const confirmText = ref('Confirm')
const cancelText = ref<string | undefined>('Cancel')
const variant = ref<'danger' | 'warning' | 'info'>('info')
let resolvePromise: ((value: boolean) => void) | null = null

// Methods
const showConfirm = (options: DialogOptions): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    isOpen.value = true
    title.value = options.title
    message.value = options.message
    confirmText.value = options.confirmText || 'Confirm'
    cancelText.value = options.cancelText
    variant.value = options.variant || 'info'
    resolvePromise = resolve
  })
}

const handleConfirm = () => {
  isOpen.value = false
  if (resolvePromise) {
    resolvePromise(true)
    resolvePromise = null
  }
}

const handleCancel = () => {
  isOpen.value = false
  if (resolvePromise) {
    resolvePromise(false)
    resolvePromise = null
  }
}

const handleClose = () => {
  handleCancel()
}

// Android hardware back closes the dialog (treated as cancel) before navigating.
useAndroidBackHandler(isOpen, handleCancel)

// Register the dialog instance with the service
setDialogRef({
  showConfirm,
})
</script>

<template>
  <ConfirmDialog
    :is-open="isOpen"
    :title="title"
    :message="message"
    :confirm-text="confirmText"
    :cancel-text="cancelText"
    :variant="variant"
    @close="handleClose"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />
</template>
